import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Table, Empty, Spin, Alert, Tag, Button, Typography, Tooltip, Modal, Select, message, Input, Checkbox, Space, Tabs } from 'antd';
import { BugOutlined, BulbOutlined, LinkOutlined, PictureOutlined, ReloadOutlined, MessageOutlined, EditOutlined, MailOutlined, CloseCircleOutlined, CheckCircleOutlined, BarChartOutlined, UnorderedListOutlined, CopyOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import feedbackService, { type Feedback, type FeedbackStatus, type FeedbackType } from '../../api/feedbackService';
import teamsService, { type Team } from '../../api/teamsService';
import eventsService, { type EventWithCircuit } from '../../api/eventsService';
import { API_BASE_URL } from '../../utils/constants';
import { parseDateOnly } from '../../utils/dateUtils';
import { useAuth } from '../../hooks/useAuth';
import type { ColumnsType } from 'antd/es/table';
import { Image } from 'antd';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import seasonsService, { type Season } from '../../api/seasonsService';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const statusOptions = [
  { value: 'open', label: 'Open' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'planned', label: 'Planned' },
  { value: 'closed', label: 'Closed' },
];

const typeIcons: Record<FeedbackType, React.ReactNode> = {
  bug: <BugOutlined style={{ color: '#f5222d' }} />,
  feature: <BulbOutlined style={{ color: '#faad14' }} />,
};

const typeLabels: Record<FeedbackType, string> = {
  bug: 'Bug Report',
  feature: 'Feature Request',
};

export const AdminFeedbackPage = () => {
  const { theme } = useTheme();
  const { authState } = useAuth();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [selectedScreenshotId, setSelectedScreenshotId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Create a map from team_id to display_name
  const teamMap = new Map<string, string>();
  teams.forEach(team => {
    teamMap.set(team.team_id, team.display_name);
  });

  // Response modal state
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);

  // Close modal state
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closingFeedback, setClosingFeedback] = useState<Feedback | null>(null);
  const [sendEmailOnClose, setSendEmailOnClose] = useState(true);
  const [closeResponseText, setCloseResponseText] = useState('');
  const [submittingClose, setSubmittingClose] = useState(false);

  // Status change modal state (for reviewed/planned)
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [statusChangeFeedback, setStatusChangeFeedback] = useState<Feedback | null>(null);
  const [newStatus, setNewStatus] = useState<FeedbackStatus | null>(null);
  const [sendEmailOnStatusChange, setSendEmailOnStatusChange] = useState(false);
  const [statusChangeMessage, setStatusChangeMessage] = useState('');
  const [submittingStatusChange, setSubmittingStatusChange] = useState(false);

  // Status filter state - default excludes 'closed'
  const [statusFilters, setStatusFilters] = useState<Set<FeedbackStatus>>(
    new Set(['open', 'reviewed', 'planned'])
  );

  // Analysis tab: main tab (list vs analysis)
  const [activeMainTab, setActiveMainTab] = useState<string>('list');
  const [analysisSeasonYear, setAnalysisSeasonYear] = useState<number | undefined>(undefined);
  const [analysisEvent, setAnalysisEvent] = useState<EventWithCircuit | null>(null);
  const [analysisEvents, setAnalysisEvents] = useState<EventWithCircuit[]>([]);
  const [analysisEventsLoading, setAnalysisEventsLoading] = useState(false);
  const [analysisSeasons, setAnalysisSeasons] = useState<Season[]>([]);

  // List tab: measure container to set table scroll height
  const listTabContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  // Toggle status filter
  const toggleStatusFilter = (status: FeedbackStatus) => {
    setStatusFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  // Filter feedback based on selected statuses
  const filteredFeedback = feedback.filter(f => statusFilters.has(f.status));

  // Feedback filtered for analysis charts: by season (year) and optionally by event date range
  const chartFeedback = useMemo(() => {
    let data = feedback;
    if (analysisSeasonYear != null) {
      data = data.filter((f) => new Date(f.created_at).getFullYear() === analysisSeasonYear);
    }
    if (analysisEvent != null) {
      const start = parseDateOnly(analysisEvent.start_date);
      const end = parseDateOnly(analysisEvent.end_date);
      end.setHours(23, 59, 59, 999);
      data = data.filter((f) => {
        const d = new Date(f.created_at);
        return d >= start && d <= end;
      });
    }
    return data;
  }, [feedback, analysisSeasonYear, analysisEvent]);

  // Get row style based on status for better UX - matching tag colors exactly
  const getRowStyle = (record: Feedback): React.CSSProperties => {
    const isDark = theme === 'dark';
    const status = record.status;
    
    switch (status) {
      case 'open':
        // Ant Design blue: #1890ff
        return {
          backgroundColor: isDark ? 'rgba(24, 144, 255, 0.12)' : 'rgba(24, 144, 255, 0.15)',
        };
      case 'reviewed':
        // Ant Design purple: #722ed1
        return {
          backgroundColor: isDark ? 'rgba(114, 46, 209, 0.12)' : 'rgba(114, 46, 209, 0.15)',
        };
      case 'planned':
        // Ant Design cyan: #13c2c2
        return {
          backgroundColor: isDark ? 'rgba(19, 194, 194, 0.12)' : 'rgba(19, 194, 194, 0.15)',
        };
      case 'closed':
        // Ant Design default gray: #d9d9d9
        return {
          backgroundColor: isDark ? 'rgba(217, 217, 217, 0.08)' : 'rgba(217, 217, 217, 0.08)',
        };
      default:
        return {};
    }
  };

  // Build screenshot URL with auth token
  const getScreenshotUrl = (feedbackId: string) => {
    return `${API_BASE_URL}/api/feedback-screenshot/${feedbackId}?token=${authState.token}`;
  };

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await feedbackService.getAllFeedback();
      if (response.success && response.data) {
        setFeedback(response.data);
      } else {
        setError('Failed to load feedback');
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const teamsData = await teamsService.getAllTeams();
      setTeams(teamsData);
    } catch (err) {
      console.error('Error fetching teams:', err);
      // Don't fail the whole page if teams fail to load
    }
  };

  const handleCopyUserEmails = async () => {
    try {
      // Collect distinct user emails from all feedback
      const userEmails = Array.from(
        new Set(
          feedback
            .map(f => f.user_email)
            .filter(email => email && email.trim() !== '')
        )
      ).sort();

      if (userEmails.length === 0) {
        message.warning('No user emails found in feedback');
        return;
      }

      // Format as comma-separated for email clients
      const emailsText = userEmails.join(', ');

      // Copy to clipboard
      await navigator.clipboard.writeText(emailsText);
      message.success(`Copied ${userEmails.length} distinct user email${userEmails.length !== 1 ? 's' : ''} to clipboard`);
    } catch (error) {
      console.error('Error copying emails:', error);
      message.error('Failed to copy emails to clipboard');
    }
  };

  useEffect(() => {
    fetchFeedback();
    fetchTeams();
  }, []);

  // Fetch seasons for analysis filter when Analysis tab is active
  useEffect(() => {
    if (activeMainTab !== 'analysis') return;
    seasonsService.getAllSeasons().then((res) => {
      if (res.success && res.data && Array.isArray(res.data)) {
        setAnalysisSeasons(res.data);
      }
    }).catch(() => setAnalysisSeasons([]));
  }, [activeMainTab]);

  // Fetch events for analysis event filter when season is selected
  useEffect(() => {
    if (analysisSeasonYear == null) {
      setAnalysisEvents([]);
      setAnalysisEvent(null);
      return;
    }
    setAnalysisEventsLoading(true);
    eventsService
      .getAllEvents(analysisSeasonYear)
      .then((res) => {
        if (res.success && res.data && Array.isArray(res.data)) {
          setAnalysisEvents(res.data);
          setAnalysisEvent(null);
        } else {
          setAnalysisEvents([]);
        }
      })
      .catch(() => setAnalysisEvents([]))
      .finally(() => setAnalysisEventsLoading(false));
  }, [analysisSeasonYear]);

  // Measure list tab container and set table body scroll height (reserve: card padding + table header + pagination)
  const TABLE_SCROLL_RESERVE_PX = 180;
  useEffect(() => {
    const el = listTabContainerRef.current;
    if (!el || activeMainTab !== 'list') return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      const y = Math.max(200, height - TABLE_SCROLL_RESERVE_PX);
      setTableScrollY(y);
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeMainTab, loading, feedback.length]);

  const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
    // If changing to 'closed', open the close modal instead
    if (newStatus === 'closed') {
      const feedbackItem = feedback.find(f => f.id === feedbackId);
      if (feedbackItem) {
        setClosingFeedback(feedbackItem);
        setCloseResponseText(feedbackItem.response || '');
        setSendEmailOnClose(true);
        setCloseModalOpen(true);
      }
      return;
    }

    // If changing to 'reviewed' or 'planned', open the status change modal
    if (newStatus === 'reviewed' || newStatus === 'planned') {
      const feedbackItem = feedback.find(f => f.id === feedbackId);
      if (feedbackItem) {
        setStatusChangeFeedback(feedbackItem);
        setNewStatus(newStatus);
        setSendEmailOnStatusChange(false);
        setStatusChangeMessage('');
        setStatusChangeModalOpen(true);
      }
      return;
    }

    // For other statuses (like 'open'), update directly without modal
    setUpdatingStatus(feedbackId);
    try {
      const response = await feedbackService.updateFeedbackStatus(feedbackId, newStatus);
      if (response.success) {
        message.success('Status updated successfully');
        // Update local state
        setFeedback(prev =>
          prev.map(f => f.id === feedbackId ? { ...f, status: newStatus } : f)
        );
      } else {
        message.error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      message.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleStatusChangeSubmit = async () => {
    if (!statusChangeFeedback || !newStatus) return;

    setSubmittingStatusChange(true);
    try {
      const response = await feedbackService.updateFeedbackStatus(
        statusChangeFeedback.id,
        newStatus,
        {
          sendEmail: sendEmailOnStatusChange,
          message: sendEmailOnStatusChange ? statusChangeMessage.trim() : undefined,
        }
      );

      if (response.success) {
        if (response.data?.emailSent) {
          message.success(`Status updated to ${newStatus} and email sent to submitter and team`);
        } else if (sendEmailOnStatusChange && !response.data?.emailSent) {
          message.warning(`Status updated to ${newStatus} but email failed to send`);
        } else {
          message.success(`Status updated to ${newStatus}`);
        }

        // Update local state
        setFeedback(prev =>
          prev.map(f => f.id === statusChangeFeedback.id ? { ...f, status: newStatus } : f)
        );

        setStatusChangeModalOpen(false);
        setStatusChangeFeedback(null);
        setNewStatus(null);
        setStatusChangeMessage('');
        setSendEmailOnStatusChange(false);
      } else {
        message.error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      message.error('Failed to update status');
    } finally {
      setSubmittingStatusChange(false);
    }
  };

  const handleCloseFeedback = async () => {
    if (!closingFeedback) return;

    // If sending email, response is required
    if (sendEmailOnClose && !closeResponseText.trim()) {
      message.error('Please provide a response when sending an email to the submitter');
      return;
    }

    setSubmittingClose(true);
    try {
      const result = await feedbackService.closeFeedback(closingFeedback.id, {
        sendEmail: sendEmailOnClose,
        response: closeResponseText.trim() || undefined,
      });

      if (result.success) {
        if (result.data?.emailSent) {
          message.success('Feedback closed and email sent to submitter and team');
        } else if (sendEmailOnClose && !result.data?.emailSent) {
          message.warning('Feedback closed but email failed to send');
        } else {
          message.success('Feedback closed');
        }

        // Update local state
        const userName = `${authState.user?.firstName} ${authState.user?.lastName}`;
        setFeedback(prev =>
          prev.map(f => f.id === closingFeedback.id
            ? {
              ...f,
              status: 'closed' as FeedbackStatus,
              response: closeResponseText.trim() || f.response,
              response_by: closeResponseText.trim() ? userName : f.response_by,
              response_at: closeResponseText.trim() ? new Date().toISOString() : f.response_at,
            }
            : f
          )
        );

        setCloseModalOpen(false);
        setClosingFeedback(null);
        setCloseResponseText('');
        setSendEmailOnClose(true);
      } else {
        message.error('Failed to close feedback');
      }
    } catch (err) {
      console.error('Error closing feedback:', err);
      message.error('Failed to close feedback');
    } finally {
      setSubmittingClose(false);
    }
  };

  const openResponseModal = (record: Feedback) => {
    setSelectedFeedback(record);
    setResponseText(record.response || '');
    setResponseModalOpen(true);
  };

  const handleResponseSubmit = async () => {
    if (!selectedFeedback || !responseText.trim()) {
      message.error('Please enter a response');
      return;
    }

    setSubmittingResponse(true);
    try {
      const result = await feedbackService.updateFeedbackResponse(selectedFeedback.id, responseText.trim());
      if (result.success) {
        message.success('Response saved successfully');
        // Update local state
        const userName = `${authState.user?.firstName} ${authState.user?.lastName}`;
        setFeedback(prev =>
          prev.map(f => f.id === selectedFeedback.id
            ? { ...f, response: responseText.trim(), response_by: userName, response_at: new Date().toISOString() }
            : f
          )
        );
        setResponseModalOpen(false);
        setSelectedFeedback(null);
        setResponseText('');
      } else {
        message.error('Failed to save response');
      }
    } catch (err) {
      console.error('Error saving response:', err);
      message.error('Failed to save response');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: ColumnsType<Feedback> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: FeedbackType) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {typeIcons[type]}
          <span>{typeLabels[type]}</span>
        </span>
      ),
      filters: [
        { text: 'Bug Report', value: 'bug' },
        { text: 'Feature Request', value: 'feature' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (title: string) => (
        <Text strong>{title}</Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Text
          type="secondary"
          style={{
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {description}
        </Text>
      ),
    },
    {
      title: 'Submitted By',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: (name: string, record: Feedback) => (
        <Tooltip title={record.user_email}>
          <Text>{name}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: 120,
      render: (team: string | null) => {
        if (!team) {
          return <Text type="secondary">—</Text>;
        }
        const displayName = teamMap.get(team) || team;
        return <Tag color="blue">{displayName}</Tag>;
      },
      filters: feedback.length > 0 ? Array.from(new Set(feedback.map(f => f.team).filter(Boolean))).map(team => {
        const displayName = teamMap.get(team as string) || team;
        return {
          text: displayName as string,
          value: team as string,
        };
      }) : [],
      onFilter: (value, record) => record.team === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: FeedbackStatus, record: Feedback) => (
        <Select
          value={status}
          style={{ width: 130 }}
          onChange={(value) => handleStatusChange(record.id, value)}
          loading={updatingStatus === record.id}
          disabled={updatingStatus === record.id}
          options={statusOptions}
          size="small"
        />
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => (
        <Text type="secondary">{formatDate(date)}</Text>
      ),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Response',
      key: 'response',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.response ? `Responded by ${record.response_by}` : 'Add response'}>
          <Button
            type={record.response ? 'link' : 'default'}
            size="small"
            icon={record.response ? <EditOutlined /> : <MessageOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openResponseModal(record);
            }}
          >
            {record.response ? 'Edit' : 'Respond'}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Attachments',
      key: 'attachments',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <span style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {record.screenshot_url && (
            <Tooltip title="View Screenshot">
              <Button
                type="text"
                size="small"
                icon={<PictureOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedScreenshotId(record.id);
                  setSelectedTitle(record.title);
                  setScreenshotModalOpen(true);
                }}
              />
            </Tooltip>
          )}
          {record.page_url && (
            <Tooltip title="Open Page">
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(record.page_url!, '_blank');
                }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  const expandedRowRender = (record: Feedback) => (
    <div style={{ padding: '8px 0' }}>
      <Paragraph
        style={{
          margin: 0,
          whiteSpace: 'pre-wrap',
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
        }}
      >
        {record.description}
      </Paragraph>

      {record.screenshot_url && (
        <div style={{ marginTop: 16 }}>
          <Button
            type="link"
            icon={<PictureOutlined />}
            onClick={() => {
              setSelectedScreenshotId(record.id);
              setSelectedTitle(record.title);
              setScreenshotModalOpen(true);
            }}
            style={{ padding: 0 }}
          >
            View Screenshot
          </Button>
        </div>
      )}

      {record.page_url && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Page URL: </Text>
          <a href={record.page_url} target="_blank" rel="noopener noreferrer">
            {record.page_url}
          </a>
        </div>
      )}

      {record.user_agent && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">User Agent: </Text>
          <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
            {record.user_agent}
          </Text>
        </div>
      )}

      {/* Admin Response */}
      {record.response && (
        <div style={{
          marginTop: 16,
          padding: 12,
          background: theme === 'dark' ? '#1a2e1a' : '#f6ffed',
          border: `1px solid ${theme === 'dark' ? '#274916' : '#b7eb8f'}`,
          borderRadius: 6,
        }}>
          <div style={{ marginBottom: 8 }}>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            <Text strong style={{ color: theme === 'dark' ? '#95de64' : '#389e0d' }}>
              Response from {record.response_by}
            </Text>
            {record.response_at && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                • {formatDate(record.response_at)}
              </Text>
            )}
          </div>
          <Paragraph
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
            }}
          >
            {record.response}
          </Paragraph>
        </div>
      )}
    </div>
  );

  // Count by status
  const statusCounts = {
    open: feedback.filter(f => f.status === 'open').length,
    reviewed: feedback.filter(f => f.status === 'reviewed').length,
    planned: feedback.filter(f => f.status === 'planned').length,
    closed: feedback.filter(f => f.status === 'closed').length,
  };

  // Chart data for analysis
  const feedbackByTeamData = useMemo(() => {
    const teamDisplay = new Map(teams.map((t) => [t.team_id, t.display_name]));
    const counts = new Map<string, number>();
    chartFeedback.forEach((f) => {
      const key = f.team ? teamDisplay.get(f.team) || f.team : 'No team';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [chartFeedback, teams]);

  const feedbackByUserData = useMemo(() => {
    const counts = new Map<string, number>();
    chartFeedback.forEach((f) => {
      const key = f.user_name || f.user_email || 'Unknown';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [chartFeedback]);

  const feedbackAcrossTimeData = useMemo(() => {
    const byDay = new Map<string, number>();
    chartFeedback.forEach((f) => {
      const d = new Date(f.created_at);
      const key = d.toISOString().slice(0, 10);
      byDay.set(key, (byDay.get(key) ?? 0) + 1);
    });
    return Array.from(byDay.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [chartFeedback]);

  const feedbackByTypeData = useMemo(() => {
    const bug = chartFeedback.filter((f) => f.type === 'bug').length;
    const feature = chartFeedback.filter((f) => f.type === 'feature').length;
    return [
      { name: typeLabels.bug, value: bug, color: '#f5222d' },
      { name: typeLabels.feature, value: feature, color: '#faad14' },
    ].filter((d) => d.value > 0);
  }, [chartFeedback]);

  const feedbackByStatusData = useMemo(() => {
    const colors: Record<FeedbackStatus, string> = {
      open: '#1890ff',
      reviewed: '#722ed1',
      planned: '#13c2c2',
      closed: '#8c8c8c',
    };
    return (['open', 'reviewed', 'planned', 'closed'] as FeedbackStatus[]).map((status) => ({
      name: statusOptions.find((o) => o.value === status)?.label ?? status,
      value: chartFeedback.filter((f) => f.status === status).length,
      color: colors[status],
    })).filter((d) => d.value > 0);
  }, [chartFeedback]);

  const CHART_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#8c8c8c'];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '8px 8px 4px 8px', flexShrink: 0 }}>
        <Card>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <Text strong style={{ fontSize: 16 }}>Feedback Management</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                View and manage all bug reports and feature requests
              </Text>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Status summary - clickable toggle buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <Tag
                  color={statusFilters.has('open') ? 'blue' : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('open') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('open')}
                >
                  {statusCounts.open} Open
                </Tag>
                <Tag
                  color={statusFilters.has('reviewed') ? 'purple' : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('reviewed') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('reviewed')}
                >
                  {statusCounts.reviewed} Reviewed
                </Tag>
                <Tag
                  color={statusFilters.has('planned') ? 'cyan' : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('planned') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('planned')}
                >
                  {statusCounts.planned} Planned
                </Tag>
                <Tag
                  color={statusFilters.has('closed') ? undefined : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('closed') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('closed')}
                >
                  {statusCounts.closed} Closed
                </Tag>
              </div>

              <Button
                icon={<CopyOutlined />}
                onClick={handleCopyUserEmails}
              >
                Copy User Emails
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchFeedback}
                loading={loading}
              />
            </div>
          </div>

          {error && (
            <Alert
              title="Error"
              description={error}
              type="error"
              closable={{ onClose: () => setError(null) }}
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      </div>

      {/* Content: List vs Analysis tabs */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0 8px 8px 8px' }}>
        <style>{`
          .admin-feedback-tabs.ant-tabs {
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          .admin-feedback-tabs > .ant-tabs-content-holder {
            flex: 1;
            margin-top: 0 !important;
            min-height: 0;
          }
          .admin-feedback-tabs > .ant-tabs-content-holder > .ant-tabs-content {
            height: 100%;
          }
          .admin-feedback-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
            height: 100%;
            overflow-y: auto;
          }
        `}</style>
        <Tabs
          activeKey={activeMainTab}
          onChange={setActiveMainTab}
          className="admin-feedback-tabs"
          style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          items={[
            {
              key: 'list',
              label: (
                <span>
                  <UnorderedListOutlined style={{ marginRight: 6 }} />
                  List
                </span>
              ),
              children: (
                <div ref={listTabContainerRef} style={{ height: '100%', paddingBottom: 8 }}>
                  {loading ? (
                    <Card style={{ textAlign: 'center', padding: 40 }}>
                      <Spin size="large" />
                    </Card>
                  ) : feedback.length === 0 ? (
                    <Card>
                      <Empty description="No feedback submitted yet" />
                    </Card>
                  ) : (
                    <Card>
                      <Table
                        dataSource={filteredFeedback}
                        columns={columns}
                        rowKey="id"
                        scroll={tableScrollY != null ? { y: tableScrollY } : undefined}
                        onRow={(record) => ({
                          style: getRowStyle(record),
                        })}
                        expandable={{
                          expandedRowRender,
                          expandRowByClick: true,
                        }}
                        pagination={{
                          pageSize: 20,
                          showSizeChanger: true,
                          showTotal: (total) => `${total} feedback item${total !== 1 ? 's' : ''}`,
                        }}
                        size="middle"
                      />
                    </Card>
                  )}
                </div>
              ),
            },
            {
              key: 'analysis',
              label: (
                <span>
                  <BarChartOutlined style={{ marginRight: 6 }} />
                  Analysis
                </span>
              ),
              children: (
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Analysis filters */}
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <Space wrap align="center">
                      <span>
                        <Text type="secondary" style={{ marginRight: 8 }}>Season:</Text>
                        <Select
                          value={analysisSeasonYear ?? '__all__'}
                          onChange={(v) => setAnalysisSeasonYear(v === '__all__' ? undefined : (v as number))}
                          style={{ minWidth: 140 }}
                          placeholder="All time"
                          options={[
                            { value: '__all__', label: 'All time' },
                            ...analysisSeasons.map((s) => ({ value: s.year, label: s.name })),
                          ]}
                        />
                      </span>
                      <span>
                        <Text type="secondary" style={{ marginRight: 8 }}>Event:</Text>
                        <Select
                          value={analysisEvent ? `${analysisEvent.season_year}-${analysisEvent.circuit_id}-${analysisEvent.start_date}` : '__all__'}
                          onChange={(key) => {
                            if (key === '__all__') {
                              setAnalysisEvent(null);
                              return;
                            }
                            const event = analysisEvents.find(
                              (e) => `${e.season_year}-${e.circuit_id}-${e.start_date}` === key
                            );
                            setAnalysisEvent(event ?? null);
                          }}
                          loading={analysisEventsLoading}
                          disabled={analysisSeasonYear == null}
                          style={{ minWidth: 260 }}
                          placeholder="All events in season"
                          options={[
                            { value: '__all__', label: 'All events in season' },
                            ...analysisEvents.map((e) => ({
                              value: `${e.season_year}-${e.circuit_id}-${e.start_date}`,
                              label: `${e.circuit.display_name} (${e.start_date} – ${e.end_date})`,
                            })),
                          ]}
                        />
                      </span>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {chartFeedback.length} feedback item{chartFeedback.length !== 1 ? 's' : ''} in range
                      </Text>
                    </Space>
                  </Card>
                  {/* Chart sub-tabs */}
                  <Tabs
                    defaultActiveKey="byTeam"
                    style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}
                    items={[
                      {
                        key: 'byTeam',
                        label: 'By Team',
                        children: (
                          <Card size="small" style={{ height: 380 }}>
                            {feedbackByTeamData.length === 0 ? (
                              <Empty description="No data for selected filters" style={{ marginTop: 60 }} />
                            ) : (
                              <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={feedbackByTeamData} layout="vertical" margin={{ left: 80, right: 24 }}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                                  <RechartsTooltip />
                                  <Bar dataKey="count" name="Feedback" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </Card>
                        ),
                      },
                      {
                        key: 'byUser',
                        label: 'By User',
                        children: (
                          <Card size="small" style={{ height: 380 }}>
                            {feedbackByUserData.length === 0 ? (
                              <Empty description="No data for selected filters" style={{ marginTop: 60 }} />
                            ) : (
                              <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={feedbackByUserData} layout="vertical" margin={{ left: 100, right: 24 }}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis type="number" />
                                  <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 11 }} />
                                  <RechartsTooltip />
                                  <RechartsTooltip />
                                  <Bar dataKey="count" name="Feedback" fill={CHART_COLORS[1]} radius={[0, 4, 4, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </Card>
                        ),
                      },
                      {
                        key: 'acrossTime',
                        label: 'Across Time',
                        children: (
                          <Card size="small" style={{ height: 380 }}>
                            {feedbackAcrossTimeData.length === 0 ? (
                              <Empty description="No data for selected filters" style={{ marginTop: 60 }} />
                            ) : (
                              <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={feedbackAcrossTimeData} margin={{ left: 24, right: 24 }}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                  <YAxis allowDecimals={false} />
                                  <RechartsTooltip />
                                  <Legend />
                                  <Line type="monotone" dataKey="count" name="Feedback" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                              </ResponsiveContainer>
                            )}
                          </Card>
                        ),
                      },
                      {
                        key: 'byType',
                        label: 'By Type',
                        children: (
                          <Card size="small" style={{ height: 380 }}>
                            {feedbackByTypeData.length === 0 ? (
                              <Empty description="No data for selected filters" style={{ marginTop: 60 }} />
                            ) : (
                              <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                  <Pie
                                    data={feedbackByTypeData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label={({ name, value }) => `${name}: ${value}`}
                                  >
                                    {feedbackByTypeData.map((_, i) => (
                                      <Cell key={i} fill={feedbackByTypeData[i].color} />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip />
                                  <Legend />
                                </PieChart>
                              </ResponsiveContainer>
                            )}
                          </Card>
                        ),
                      },
                      {
                        key: 'byStatus',
                        label: 'By Status',
                        children: (
                          <Card size="small" style={{ height: 380 }}>
                            {feedbackByStatusData.length === 0 ? (
                              <Empty description="No data for selected filters" style={{ marginTop: 60 }} />
                            ) : (
                              <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                  <Pie
                                    data={feedbackByStatusData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label={({ name, value }) => `${name}: ${value}`}
                                  >
                                    {feedbackByStatusData.map((_, i) => (
                                      <Cell key={i} fill={feedbackByStatusData[i].color} />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip />
                                  <Legend />
                                </PieChart>
                              </ResponsiveContainer>
                            )}
                          </Card>
                        ),
                      },
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Screenshot Preview Modal */}
      <Modal
        title={`Screenshot: ${selectedTitle}`}
        open={screenshotModalOpen}
        onCancel={() => {
          setScreenshotModalOpen(false);
          setSelectedScreenshotId(null);
          setSelectedTitle('');
        }}
        footer={null}
        width={800}
        centered
      >
        {selectedScreenshotId && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={getScreenshotUrl(selectedScreenshotId)}
              alt="Feedback screenshot"
              style={{ maxWidth: '100%', borderRadius: 4 }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
            />
          </div>
        )}
      </Modal>

      {/* Response Modal */}
      <Modal
        title={selectedFeedback?.response ? 'Edit Response' : 'Add Response'}
        open={responseModalOpen}
        onCancel={() => {
          setResponseModalOpen(false);
          setSelectedFeedback(null);
          setResponseText('');
        }}
        onOk={handleResponseSubmit}
        okText={selectedFeedback?.response ? 'Update Response' : 'Send Response'}
        confirmLoading={submittingResponse}
        width={600}
      >
        {selectedFeedback && (
          <div>
            {/* Original feedback summary */}
            <div style={{
              marginBottom: 16,
              padding: 12,
              background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
              borderRadius: 6,
            }}>
              <Text strong>{selectedFeedback.title}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                From: {selectedFeedback.user_name} • {formatDate(selectedFeedback.created_at)}
              </Text>
              <Paragraph
                style={{
                  marginTop: 8,
                  marginBottom: 0,
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedFeedback.description}
              </Paragraph>
            </div>

            {/* Response input */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Your Response:</Text>
              <TextArea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Enter your response to this feedback..."
                rows={4}
                maxLength={2000}
                showCount
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Close Feedback Modal */}
      <Modal
        title={
          <Space>
            <CloseCircleOutlined style={{ color: '#52c41a' }} />
            Close Feedback
          </Space>
        }
        open={closeModalOpen}
        onCancel={() => {
          setCloseModalOpen(false);
          setClosingFeedback(null);
          setCloseResponseText('');
          setSendEmailOnClose(true);
        }}
        onOk={handleCloseFeedback}
        okText={sendEmailOnClose ? 'Close & Send Email' : 'Close Feedback'}
        okButtonProps={{
          icon: sendEmailOnClose ? <MailOutlined /> : undefined,
          disabled: sendEmailOnClose && !closeResponseText.trim(),
        }}
        confirmLoading={submittingClose}
        width={650}
      >
        {closingFeedback && (
          <div>
            {/* Original feedback summary */}
            <div style={{
              marginBottom: 16,
              padding: 12,
              background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
              borderRadius: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {typeIcons[closingFeedback.type]}
                <Text strong>{closingFeedback.title}</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                From: {closingFeedback.user_name} ({closingFeedback.user_email})
              </Text>
              <Paragraph
                style={{
                  marginTop: 8,
                  marginBottom: 0,
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                }}
                ellipsis={{ rows: 3, expandable: true }}
              >
                {closingFeedback.description}
              </Paragraph>
            </div>

            {/* Send email option */}
            <div style={{
              marginBottom: 16,
              padding: 12,
              background: theme === 'dark' ? '#162312' : '#f6ffed',
              border: `1px solid ${theme === 'dark' ? '#274916' : '#b7eb8f'}`,
              borderRadius: 6,
            }}>
              <Checkbox
                checked={sendEmailOnClose}
                onChange={(e) => setSendEmailOnClose(e.target.checked)}
              >
                <span style={{ fontWeight: 500 }}>
                  <MailOutlined style={{ marginRight: 8 }} />
                  Send email notification to {closingFeedback.user_name} and team
                </span>
              </Checkbox>
              <div style={{ marginLeft: 24, marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  The submitter and team members will receive an email with your response below.
                </Text>
              </div>
            </div>

            {/* Response input */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Response to Submitter:
                {sendEmailOnClose && <span style={{ color: '#ff4d4f' }}> *</span>}
              </Text>
              <TextArea
                value={closeResponseText}
                onChange={(e) => setCloseResponseText(e.target.value)}
                placeholder={sendEmailOnClose
                  ? "Enter your response (required when sending email)..."
                  : "Optional: Enter a response for your records..."
                }
                rows={5}
                maxLength={2000}
                showCount
                status={sendEmailOnClose && !closeResponseText.trim() ? 'error' : undefined}
              />
              {sendEmailOnClose && !closeResponseText.trim() && (
                <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                  A response is required when sending an email to the submitter
                </Text>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Status Change Modal (for reviewed/planned) */}
      <Modal
        title={
          <Space>
            <MailOutlined style={{ color: newStatus === 'reviewed' ? '#722ed1' : '#13c2c2' }} />
            Update Status to {newStatus === 'reviewed' ? 'Reviewed' : 'Planned'}
          </Space>
        }
        open={statusChangeModalOpen}
        onCancel={() => {
          setStatusChangeModalOpen(false);
          setStatusChangeFeedback(null);
          setNewStatus(null);
          setStatusChangeMessage('');
          setSendEmailOnStatusChange(false);
        }}
        onOk={handleStatusChangeSubmit}
        okText={sendEmailOnStatusChange ? `Update & Send Email` : 'Update Status'}
        okButtonProps={{
          icon: sendEmailOnStatusChange ? <MailOutlined /> : undefined,
        }}
        confirmLoading={submittingStatusChange}
        width={650}
      >
        {statusChangeFeedback && newStatus && (
          <div>
            {/* Original feedback summary */}
            <div style={{
              marginBottom: 16,
              padding: 12,
              background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
              borderRadius: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {typeIcons[statusChangeFeedback.type]}
                <Text strong>{statusChangeFeedback.title}</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                From: {statusChangeFeedback.user_name} ({statusChangeFeedback.user_email})
              </Text>
              <Paragraph
                style={{
                  marginTop: 8,
                  marginBottom: 0,
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                }}
                ellipsis={{ rows: 3, expandable: true }}
              >
                {statusChangeFeedback.description}
              </Paragraph>
            </div>

            {/* Send email option */}
            <div style={{
              marginBottom: 16,
              padding: 12,
              background: theme === 'dark' 
                ? (newStatus === 'reviewed' ? '#1f0f2e' : '#0f1f1f')
                : (newStatus === 'reviewed' ? '#f9f0ff' : '#e6fffb'),
              border: `1px solid ${theme === 'dark' 
                ? (newStatus === 'reviewed' ? '#391085' : '#0d4d4d')
                : (newStatus === 'reviewed' ? '#d3adf7' : '#87e8de')}`,
              borderRadius: 6,
            }}>
              <Checkbox
                checked={sendEmailOnStatusChange}
                onChange={(e) => setSendEmailOnStatusChange(e.target.checked)}
              >
                <span style={{ fontWeight: 500 }}>
                  <MailOutlined style={{ marginRight: 8 }} />
                  Send email notification to {statusChangeFeedback.user_name} and team
                </span>
              </Checkbox>
              <div style={{ marginLeft: 24, marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  The submitter and team members will receive an email about the status change.
                </Text>
              </div>
            </div>

            {/* Optional message input */}
            {sendEmailOnStatusChange && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Optional Message:
                </Text>
                <TextArea
                  value={statusChangeMessage}
                  onChange={(e) => setStatusChangeMessage(e.target.value)}
                  placeholder="Optional: Add a message to include in the email notification..."
                  rows={4}
                  maxLength={2000}
                  showCount
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                  This message will be included in the email but will not be saved as a response.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

